import { Shape, ConnectionPoint } from '../types/Shape';

// 도형의 연결점 계산 함수
export const getConnectionPoints = (shape: Shape) => {
  if (shape.type === 'rect' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'code' || shape.type === 'md' || shape.type === 'youtube') {
    const w = shape.width || 0;
    const h = shape.height || 0;
    return {
      top: { x: shape.x + w / 2, y: shape.y },
      right: { x: shape.x + w, y: shape.y + h / 2 },
      bottom: { x: shape.x + w / 2, y: shape.y + h },
      left: { x: shape.x, y: shape.y + h / 2 }
    };
  } else if (shape.type === 'circle') {
    // 원도 사각형 기반이므로 중심점 계산
    const w = shape.width || 0;
    const h = shape.height || 0;
    const centerX = shape.x + w / 2;
    const centerY = shape.y + h / 2;
    const radiusX = w / 2;
    const radiusY = h / 2;
    
    return {
      top: { x: centerX, y: centerY - radiusY },
      right: { x: centerX + radiusX, y: centerY },
      bottom: { x: centerX, y: centerY + radiusY },
      left: { x: centerX - radiusX, y: centerY }
    };
  }
  return {};
};

// 가장 가까운 연결점 찾기 (연결점 타입과 도형 ID 포함)
export const findNearestConnectionPoint = (
  x: number, 
  y: number, 
  shapes: Shape[], 
  snapDistance: number = 20
): ConnectionPoint | null => {
  let nearestPoint: ConnectionPoint | null = null;
  let minDistance = snapDistance;
  
  shapes.forEach(shape => {
    if (shape.type === 'rect' || shape.type === 'circle' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'code' || shape.type === 'md' || shape.type === 'youtube') {
      const connectionPoints = getConnectionPoints(shape);
      Object.entries(connectionPoints).forEach(([pointType, point]) => {
        const distance = Math.sqrt(
          Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearestPoint = {
            x: point.x,
            y: point.y,
            shapeId: shape.id,
            pointType: pointType as 'top' | 'right' | 'bottom' | 'left'
          };
        }
      });
    }
  });
  
  return nearestPoint;
};

// 연결점 근처에 있는지 확인하는 함수
export const isNearConnectionPoint = (
  x: number, 
  y: number, 
  shapes: Shape[], 
  threshold: number = 30
): boolean => {
  return shapes.some(shape => {
    if (shape.type === 'rect' || shape.type === 'circle' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'code' || shape.type === 'md' || shape.type === 'youtube') {
      const connectionPoints = getConnectionPoints(shape);
      return Object.values(connectionPoints).some(point => {
        const distance = Math.sqrt(
          Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
        );
        return distance < threshold;
      });
    }
    return false;
  });
};

// 포인트가 객체 위에 있는지 확인하고 객체 ID 반환
export const findShapeAtPoint = (
  x: number, 
  y: number, 
  shapes: Shape[]
): Shape | null => {
  // 역순으로 검색 (위에 있는 객체 우선)
  for (let i = shapes.length - 1; i >= 0; i--) {
    const shape = shapes[i];
    
    if (shape.type === 'rect' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'code' || shape.type === 'md') {
      const w = shape.width || 0;
      const h = shape.height || 0;
      if (x >= shape.x && x <= shape.x + w && y >= shape.y && y <= shape.y + h) {
        return shape;
      }
    } else if (shape.type === 'circle') {
      // 원도 사각형 기반이므로 중심점 기준으로 거리 계산
      const w = shape.width || 0;
      const h = shape.height || 0;
      const centerX = shape.x + w / 2;
      const centerY = shape.y + h / 2;
      const radiusX = w / 2;
      const radiusY = h / 2;
      
      // 타원 내부인지 확인 (정규화된 거리)
      const normalizedDistance = Math.pow((x - centerX) / radiusX, 2) + Math.pow((y - centerY) / radiusY, 2);
      if (normalizedDistance <= 1) {
        return shape;
      }
    }
  }
  
  return null;
};

// 객체 내에서의 상대 좌표 계산 (0.0 ~ 1.0)
export const getRelativePosition = (
  x: number, 
  y: number, 
  shape: Shape
): { relativeX: number; relativeY: number } | null => {
  if (shape.type === 'rect' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'circle' || shape.type === 'code' || shape.type === 'md') {
    // 원도 사각형 기반이므로 동일한 로직 사용
    const w = shape.width || 0;
    const h = shape.height || 0;
    if (w === 0 || h === 0) return null;
    
    return {
      relativeX: (x - shape.x) / w,
      relativeY: (y - shape.y) / h
    };
  }
  
  return null;
};

// 상대 좌표를 절대 좌표로 변환
export const getAbsolutePosition = (
  relativeX: number, 
  relativeY: number, 
  shape: Shape
): { x: number; y: number } => {
  if (shape.type === 'rect' || shape.type === 'pdf' || shape.type === 'image' || shape.type === 'circle' || shape.type === 'code' || shape.type === 'md') {
    // 원도 사각형 기반이므로 동일한 로직 사용
    const w = shape.width || 0;
    const h = shape.height || 0;
    
    return {
      x: shape.x + relativeX * w,
      y: shape.y + relativeY * h
    };
  }
  
  return { x: shape.x, y: shape.y };
};
